import { useLayoutEffect, useRef } from "react";
import { opisPostepuWysylania } from "../../lib/formularzWejscia.js";
import type { PostepWgrywania } from "../../lib/wgrywanie.js";
import { IconOstrzezenie } from "../icons/index.js";
import { Button } from "../ui/Button.js";
import { ProgressBar } from "../ui/ProgressBar.js";

/**
 * Postep wysylania nagrania w karcie "Nowa analiza" (PR-1..PR-5): pasek PR-2 z etykieta "co teraz" i procentem, pod nim
 * wiersz ze szczegolami (numer czesci, predkosc, czas do konca - ostatnie dwa tylko gdy wiarygodne) i przycisk
 * "Anuluj wysylanie".
 *
 * PR-3: pasek siedzi w karcie i nie blokuje ekranu; anulowanie jest zawsze pod reka, dopoki ma sens. Po zlozeniu pliku
 * na serwerze przycisk znika (analiza moze juz powstawac, przerwanie nie cofnie jej).
 * PR-5: czytnik slyszy tylko to, co oglasza ProgressBar (co ~10%), a zdanie o ponowieniu to osobny status. Pojawia sie
 * wylacznie w czasie awarii lacza, a kolejne ponowienia dzieli co najmniej sekunda i odstepy rosna (1/2/4/8/16 s),
 * wiec czytnik nie jest zalewany.
 *
 * A-4 / K-8: gdy "Anuluj wysylanie" znika (przejscie do skladania pliku), a fokus byl na nim, nie moze spasc na <body>.
 * Przenosimy go na zdanie o skladaniu: to region role="status" (tabIndex -1), wiec czytnik od razu slyszy, co sie stalo.
 *
 * Calosc tekstu liczy czysta funkcja `opisPostepuWysylania`; tu jest tylko uklad.
 */
export function PostepWysylania({
  postep,
  onAnuluj,
}: {
  postep: PostepWgrywania;
  onAnuluj: () => void;
}): React.JSX.Element {
  const opis = opisPostepuWysylania(postep);
  const skladanie = postep.faza === "skladanie";
  const regionSkladaniaRef = useRef<HTMLParagraphElement>(null);
  const mogloAnulowacRef = useRef(opis.mozeAnulowac);

  useLayoutEffect(() => {
    const bylo = mogloAnulowacRef.current;
    mogloAnulowacRef.current = opis.mozeAnulowac;
    if (!bylo || opis.mozeAnulowac) return;
    // Przycisk wlasnie zniknal. Jesli fokus byl na nim, przegladarka oddala go <body>; jesli uzytkownik byl gdzie indziej
    // (np. w innym polu), nie ruszamy go.
    const aktywny = document.activeElement;
    if (aktywny === null || aktywny === document.body) regionSkladaniaRef.current?.focus();
  }, [opis.mozeAnulowac]);

  return (
    <div className="postep-wysylania">
      <ProgressBar
        etykieta={opis.etykieta}
        procent={opis.procent}
        akcja={
          <>
            {skladanie ? (
              <p className="postep-wysylania__szczegoly" role="status" tabIndex={-1} ref={regionSkladaniaRef}>
                <span className="tylko-dla-czytnika">Wszystkie części wysłane. </span>
                {opis.szczegoly}
              </p>
            ) : (
              <p className="postep-wysylania__szczegoly">{opis.szczegoly}</p>
            )}
            {opis.mozeAnulowac && (
              <Button wariant="secondary" onClick={onAnuluj}>
                Anuluj wysyłanie
              </Button>
            )}
          </>
        }
      />
      {opis.ponowienie && (
        <p className="postep-wysylania__ponowienie" role="status">
          <IconOstrzezenie size={14} />
          {opis.ponowienie}
        </p>
      )}
    </div>
  );
}
